import { Box, styled, Typography } from '@mui/material'
import { useEffect, useMemo } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import ClientLogo from 'components/ClientLogo'
import { FloatingInput, Button, Loader } from '@icanbwell/ui-toolkit'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as Yup from 'yup'
import { useValidateResetTokenMutation, useResetPasswordMutation } from '@client-hub/store/api/crs/react'
import ROUTES from '@client-hub/shared/constants/routes'
import loginBackground from 'assets/login_background.png'
import Link from '@client-hub/shared/react/components/Link'
import { get } from 'lodash'
import { useTranslation } from 'react-i18next'
import NewPasswordField, {
  useSchema as useNewPasswordSchema,
} from '@client-hub/shared/react/components/Forms/Fields/NewPassword'

const Wrapper = styled(Box)({
  background: `linear-gradient(0deg, rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url(${loginBackground}) no-repeat fixed center`,
  minHeight: '100vh',
  backgroundSize: 'cover',
  display: 'flex',
  alignItems: 'center',
  flexDirection: 'column',
})

const useChangePasswordSchema = () => {
  const { t } = useTranslation()
  const newPasswordSchema = useNewPasswordSchema()
  return Yup.object({
    password: newPasswordSchema.required(t('forms.validations.requiredPassword')),
    confirm_password: Yup.string()
      .required(t('forms.validations.requiredConfirmPassword'))
      .when('password', {
        is: (password: string) => !!password,
        then: Yup.string()
          .oneOf([Yup.ref('password'), null], t('forms.validations.bothPasswordsShouldMatch'))
          .required(t('forms.validations.requiredConfirmPassword')),
      }),
  })
}
export type CredentialsData = Yup.InferType<ReturnType<typeof useChangePasswordSchema>>

const ResetPassword = () => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const changePasswordSchema = useChangePasswordSchema()
  const [searchParams] = useSearchParams()
  const [
    validateResetToken,
    {
      isLoading: validateResetTokenLoading,
      isSuccess: validateResetTokenSuccess,
      error: validateResetTokenError,
      data: validResetTokenData,
    },
  ] = useValidateResetTokenMutation()
  const [resetPassword, { data, isLoading: resetPasswordLoading, isSuccess: resetPasswordSuccess }] =
    useResetPasswordMutation()
  const form = useForm<CredentialsData>({
    criteriaMode: 'all',
    resolver: yupResolver(changePasswordSchema),
  })
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = form

  const token = searchParams.get('token') || ''

  const isPasswordUpdated = useMemo(() => {
    if (validateResetTokenSuccess && validResetTokenData) {
      return validResetTokenData.is_password_updated
    }
    return false
  }, [validateResetTokenSuccess, validResetTokenData])

  useEffect(() => {
    // token doesnt exist, redirect back to /login
    if (!token) {
      navigate('/login')
    }
    // check if the token is valid or not
    validateResetToken({ token })
  }, [navigate, searchParams, validateResetToken, token])

  useEffect(() => {
    if (!validateResetTokenSuccess && validateResetTokenError) {
      navigate('/login')
      return
    }
  }, [validateResetTokenSuccess, validateResetTokenError, navigate])

  useEffect(() => {
    if (resetPasswordSuccess && data) {
      const { results, uid = '', two_factor_auth = 0, sms_status } = data
      // MFA enforced if no "results" field from server
      if (results) {
        navigate(ROUTES.BASE, { replace: true })
      } else {
        if (two_factor_auth) {
          const state = {
            uid,
            two_factor_auth,
            sms_status,
            isAllowedMfa: true,
          }
          navigate(ROUTES.LOGIN.MFA.BASE, { state })
        } else {
          const state = { uid, two_factor_auth, isAllowedMfa: true }
          navigate(ROUTES.MFA.SETUP.BASE, { state })
        }
      }
    }
  }, [resetPasswordSuccess, navigate, data])

  const onSubmit = (data: CredentialsData) => {
    resetPassword({
      ...data,
      token,
    })
  }

  return (
    <Wrapper pt={6}>
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        maxWidth={350}
        minHeight={500}
      >
        {validateResetTokenLoading || resetPasswordLoading ? (
          <Loader  />
        ) : (
          <>
            <ClientLogo style={{ maxHeight: '10em' }} width="20em" />
            <Box color="white" fontSize="1.5em" textAlign="center" my={3}>
              {isPasswordUpdated ? t('routes.resetPassword.title') : t('routes.setPassword.title')}
            </Box>

            {
              <Box
                sx={{ '& > div': { mb: 2 }, width: '20em' }}
                component="form"
                autoComplete="off"
                display="flex"
                flexDirection="column"
                onSubmit={handleSubmit(onSubmit)}
              >
                <NewPasswordField
                  {...register('password')}
                  id="credentials-password"
                  label={t('forms.credentials.password')}
                  error={!!errors.password}
                  helperText={errors.password?.message}
                  form={form}
                />

                <FloatingInput
                  id="credentials-confirm-password"
                  type="password"
                  label={t('forms.credentials.confirmPassword')}
                  {...register('confirm_password')}
                  error={!!errors.confirm_password}
                  helperText={errors.confirm_password?.message}
                />

                <Button
                  sx={{ mt: 3 }}
                  width="100%"
                  id="credentials-submit-button"
                  type="submit"
                  text={isPasswordUpdated ? t('forms.credentials.resetPassword') : t('forms.credentials.setPassword')}
                  disabled={isSubmitting}
                />
              </Box>
            }

            <Box mt={2}>
              <Link to="/login">
                <Typography sx={{ color: 'white', fontSize: '1.2rem' }}>{t('register.returnToSignIn')}</Typography>
              </Link>
            </Box>
          </>
        )}
      </Box>
    </Wrapper>
  )
}

export default ResetPassword
